import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Table } from "../design-system";
import { useApi } from "../data/api";
import { useAuth } from "../auth/AuthProvider";
import { useCart } from "../data/cartStore";
import { discountLabel, discountLabelWithPct } from "../lib/discount_labels";

function money(v) { return `€ ${Number(v).toFixed(2)}`; }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : "—"; }
function fmtDateTime(iso) { return iso ? new Date(iso).toLocaleString("it-IT") : "—"; }

const STATUS_LABELS = {
  RECEIVED: "Ricevuto", WAITING_PAYMENT: "Attesa pagamento", PROCESSING: "In lavorazione",
  SHIPPING: "In consegna", IN_DELIVERY: "In consegna",
  DELIVERED: "Consegnato", READY_FOR_PICKUP: "Pronto per ritiro",
  PICKED_UP: "Ritirato", EXPIRED_UNPAID: "Scaduto (non pagato)",
  CANCELLED: "Annullato", VOIDED: "Stornato",
};
const STATUS_TONE = {
  DELIVERED: "success", PICKED_UP: "success", SHIPPING: "info", IN_DELIVERY: "info",
  PROCESSING: "info", CANCELLED: "danger", VOIDED: "danger",
  EXPIRED_UNPAID: "warning", WAITING_PAYMENT: "warning",
};

const SHIPMENT_STATUS_LABELS = {
  DRAFT: "In preparazione", IN_TRANSIT: "In consegna",
  DELIVERED: "Consegnata", CLOSED: "Chiusa", VOIDED: "Annullata",
};

const FULFILLMENT_LABELS = {
  NOT_FULFILLED: "Da evadere", PARTIAL: "Evasione parziale", COMPLETE: "Evasione completa",
};

export function MyOrdersPage() {
  const api = useApi();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.myOrders().then(setOrders).catch(() => setOrders([])); }, []);
  return (
    <Section title="I miei ordini">
      {!orders && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
      {orders && orders.length === 0 && <Alert tone="info">Non hai ancora nessun ordine.</Alert>}
      {orders && orders.length > 0 && (
        <Table
          columns={[
            { key: "number", label: "Numero", render: (r) => <Link data-testid={`order-link-${r.number}`} to={`/customer/orders/${r.id}`}>{r.number}</Link> },
            { key: "status", label: "Stato", render: (r) => <Badge tone={STATUS_TONE[r.status] || "info"}>{STATUS_LABELS[r.status]}</Badge> },
            { key: "fulfillment_status", label: "Evasione", render: (r) => FULFILLMENT_LABELS[r.fulfillment_status] || r.fulfillment_status },
            { key: "total", label: "Totale", render: (r) => money(r.total) },
            { key: "created_at", label: "Creato", render: (r) => fmtDate(r.created_at) },
          ]}
          rows={orders}
        />
      )}
    </Section>
  );
}

// ---------- DDT Download button (server-side authorized streaming) ----------
function DdtDownloadButton({ ddt, shipmentNumber }) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const doDownload = async () => {
    setBusy(true); setErr(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddt/${ddt.id}/download`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        setErr(r.status === 403 ? "Non autorizzato" : "Download DDT non disponibile");
        return;
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DDT-${ddt.number}.pdf`;
      a.click();
    } catch (e) {
      setErr("Errore download");
    } finally { setBusy(false); }
  };
  return (
    <span>
      <Button
        size="sm"
        onClick={doDownload}
        loading={busy} disabled={busy}
        data-testid={`ddt-download-btn-${ddt.number}`}
      >
        {busy ? "Attendi…" : "Scarica DDT"}
      </Button>
      {err && <span className="muted" style={{ marginLeft: 8, color: "#b00" }}>{err}</span>}
    </span>
  );
}

// ---------- Shipment card ----------
function ShipmentCard({ shipment, lines }) {
  const isInTransit = shipment.status === "IN_TRANSIT";
  const isDelivered = shipment.status === "DELIVERED" || shipment.status === "CLOSED";
  const lineById = Object.fromEntries((lines || []).map((l) => [l.order_line_id, l]));

  // Per-shipment partial rows (from shipment lines)
  const shpLines = shipment.lines || [];

  return (
    <Card data-testid={`shipment-card-${shipment.number}`} style={{ marginBottom: 16 }}>
      <Card.Header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <strong>Spedizione {shipment.number}</strong>{" "}
            <Badge tone={isInTransit ? "info" : isDelivered ? "success" : "neutral"}>
              {SHIPMENT_STATUS_LABELS[shipment.status] || shipment.status}
            </Badge>
          </div>
          <div className="muted" style={{ fontSize: ".9rem" }}>
            {shipment.load_plan?.confirmed_at && <>Piano confermato il {fmtDateTime(shipment.load_plan.confirmed_at)}</>}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 12 }}>
          <div><div className="muted">Vettore</div><div><strong>{shipment.carrier?.business_name || "—"}</strong></div></div>
          <div><div className="muted">Data carico</div><div>{fmtDate(shipment.loading_date)}</div></div>
          <div><div className="muted">Data prevista consegna</div><div>{fmtDate(shipment.expected_delivery_date)}</div></div>
          <div><div className="muted">Fascia oraria</div><div>{shipment.delivery_window || "—"}</div></div>
          <div><div className="muted">Pallet</div><div>{shipment.actual_pallets ?? shipment.estimated_pallets ?? "—"}</div></div>
          <div><div className="muted">Kg spediti</div><div>{shipment.shipped_weight_kg || "—"}</div></div>
        </div>

        {/* DDT list */}
        {shipment.ddts?.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <div className="muted" style={{ fontSize: ".9rem", marginBottom: 6 }}>Documenti Di Trasporto</div>
            <div className="ds-table-wrap">
              <table className="ds-table">
                <thead><tr><th>Numero DDT</th><th>Data</th><th style={{ width: 200 }}></th></tr></thead>
                <tbody>
                  {shipment.ddts.map((d) => (
                    <tr key={d.id} data-testid={`ddt-row-${d.number}`}>
                      <td><code>{d.number}</code></td>
                      <td>{fmtDate(d.date)}</td>
                      <td><DdtDownloadButton ddt={d} shipmentNumber={shipment.number} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (isInTransit || isDelivered) && (
          <Alert tone="warning">DDT non ancora disponibile per questa spedizione.</Alert>
        )}

        {/* Per-shipment lines (only if there are multiple shipments or a partial fulfillment) */}
        {shpLines.length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted" style={{ cursor: "pointer" }}>Righe spedite ({shpLines.length})</summary>
            <div className="ds-table-wrap" style={{ marginTop: 8 }}>
              <table className="ds-table">
                <thead><tr><th>SKU</th><th>Prodotto</th><th>Cartoni spediti</th></tr></thead>
                <tbody>
                  {shpLines.map((sl, i) => {
                    const ol = lineById[sl.order_line_id];
                    return (
                      <tr key={i}>
                        <td>{ol?.sku || "—"}</td>
                        <td>{ol?.name || "—"}</td>
                        <td>{sl.qty_cartons_shipped}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </Card.Body>
    </Card>
  );
}

export function OrderDetailPage() {
  const { id } = useParams();
  const api = useApi();
  const cart = useCart();
  const nav = useNavigate();
  const { user } = useAuth();
  // v34.30 — Logistics operators reuse this page in read-only mode. Hide the
  // Customer-only "Riordina" CTA when the viewer is not a Customer.
  const isCustomerViewer = (user?.roles || []).includes("CUSTOMER");
  const [detail, setDetail] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [reorderErr, setReorderErr] = useState(null);
  const [contractSource, setContractSource] = useState(null);

  useEffect(() => {
    api.orderDetail(id)
      .then(setDetail)
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
    api.orderTracking(id).then(setTracking).catch(() => setTracking(null));
    // Contract-source panel (visible only to ADMIN/SALES; endpoint returns 403 otherwise)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/contract-source`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }).then(r => r.status === 200 ? r.json() : null).then(setContractSource).catch(() => {});
  }, [id]);

  const doReorder = async () => {
    setReordering(true); setReorderErr(null);
    try {
      const preview = await api.reorder({ order_id: id, delivery_mode: detail.delivery_mode });
      const lines = preview.lines.map((l) => ({
        product_id: l.product_id, sku: "", name: "", qty_cartons: l.qty_cartons, kg_per_carton: 0,
      }));
      const products = await api.catalog();
      const byId = Object.fromEntries(products.map((p) => [p.id, p]));
      const enriched = lines.map((l) => ({ ...l, sku: byId[l.product_id]?.sku || "", name: byId[l.product_id]?.name || "", kg_per_carton: parseFloat(byId[l.product_id]?.kg_per_carton || 0) }));
      cart.clear();
      cart.replaceLines(enriched);
      if (preview.unavailable_products?.length > 0) {
        alert(`Attenzione: ${preview.unavailable_products.length} prodotto/i non più disponibili sono stati esclusi.`);
      }
      nav("/customer/cart");
    } catch (e) {
      setReorderErr(e?.response?.data?.detail || "Errore riordino");
    } finally { setReordering(false); }
  };

  if (notFound) return (
    <Section title="Ordine non trovato">
      <Alert tone="warning" data-testid="order-not-found">
        Questo ordine non esiste o non è accessibile al tuo account.
      </Alert>
      <Link to="/customer/orders"><Button variant="ghost">← Torna ai miei ordini</Button></Link>
    </Section>
  );
  if (!detail) return <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>;

  const order = tracking?.order || detail;
  const shipments = tracking?.shipments || [];
  const trackingLines = tracking?.lines || [];
  const isPartial = order.fulfillment_status === "PARTIAL";

  return (
    <>
      <Section title={`Ordine ${detail.number}`}
               data-testid="order-detail-page"
               actions={<div style={{ display: "flex", gap: ".5rem" }}>
                 {isCustomerViewer && (
                   <Button data-testid="reorder-btn" variant="secondary" onClick={doReorder} disabled={reordering}>
                     {reordering ? "Attendi…" : "Riordina"}
                   </Button>
                 )}
                 <Link to={isCustomerViewer ? "/customer/orders" : "/logistics"}>
                   <Button variant="ghost">← Torna</Button>
                 </Link>
               </div>}
               subtitle={
                 <span>
                   <Badge tone={STATUS_TONE[order.status] || "info"} data-testid="order-status-badge">
                     {STATUS_LABELS[order.status]}
                   </Badge>
                   {" · "}
                   Totale <strong>{money(detail.total)}</strong>
                   {isPartial && <> · <Badge tone="warning">Evasione parziale</Badge></>}
                 </span>}>
        {reorderErr && <Alert tone="warning">{reorderErr}</Alert>}
        {order.status === "SHIPPING" && shipments.some((s) => s.status === "IN_TRANSIT") && (
          <Alert tone="info" data-testid="in-delivery-alert">
            Il tuo ordine è <strong>in consegna</strong>.
            {shipments[0].expected_delivery_date && <> Data prevista: <strong>{fmtDate(shipments[0].expected_delivery_date)}</strong>.</>}
          </Alert>
        )}
        {(order.status === "SHIPPING" || order.status === "IN_DELIVERY") && (
          <Alert tone="info" data-testid="in-delivery-alert">
            🚚 Il tuo ordine è <strong>in consegna</strong> con il vettore.
            {shipments[0]?.expected_delivery_date && <> Data prevista di consegna: <strong>{fmtDate(shipments[0].expected_delivery_date)}</strong>.</>}
          </Alert>
        )}
      </Section>

      {/* v42 (Mar 2026) — Data prevista / richiesta / confermata di consegna.
          Snapshot storici salvati sull'ordine alla conferma. Non ricalcolate. */}
      {(order.estimated_delivery_date || order.requested_delivery_date || order.confirmed_delivery_date) && (
        <Section title="Date di consegna">
          <Card data-testid="order-delivery-dates-card">
            <Card.Body>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
                {order.estimated_delivery_date && (
                  <div data-testid="order-estimated-delivery-date">
                    <div className="muted" style={{ fontSize: ".85rem" }}>Data prevista di consegna</div>
                    <div style={{ fontWeight: 600 }}>{fmtDate(order.estimated_delivery_date)}</div>
                  </div>
                )}
                {order.requested_delivery_date && (
                  <div data-testid="order-requested-delivery-date">
                    <div className="muted" style={{ fontSize: ".85rem" }}>Data richiesta dal cliente</div>
                    <div style={{ fontWeight: 600 }}>{fmtDate(order.requested_delivery_date)}</div>
                  </div>
                )}
                {order.confirmed_delivery_date && (
                  <div data-testid="order-confirmed-delivery-date">
                    <div className="muted" style={{ fontSize: ".85rem" }}>Data confermata di consegna</div>
                    <div style={{ fontWeight: 600 }}>
                      {fmtDate(order.confirmed_delivery_date)}
                      {order.confirmed_delivery_window ? ` · ${order.confirmed_delivery_window}` : ""}
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Section>
      )}

      {order.delivery_mode === "PICKUP" && order.pickup_location_snapshot && (
        <Section title="Sede di ritiro">
          <Card data-testid="pickup-location-card"><Card.Body>
            {order.pickup_location_snapshot.warehouse_name && (
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {order.pickup_location_snapshot.warehouse_name}
              </div>
            )}
            {order.pickup_location_snapshot.address && (
              <div>{order.pickup_location_snapshot.address}</div>
            )}
            {(order.pickup_location_snapshot.zip_code || order.pickup_location_snapshot.city) && (
              <div>
                {order.pickup_location_snapshot.zip_code} {order.pickup_location_snapshot.city}
                {order.pickup_location_snapshot.province ? ` (${order.pickup_location_snapshot.province})` : ""}
              </div>
            )}
            {order.pickup_location_snapshot.phone && (
              <div>Tel. {order.pickup_location_snapshot.phone}</div>
            )}
            {order.pickup_location_snapshot.pickup_hours && (
              <div className="muted" style={{ fontSize: ".85rem", marginTop: 6 }}>
                Orari ritiro: {order.pickup_location_snapshot.pickup_hours}
              </div>
            )}
            {order.pickup_location_snapshot.notes && (
              <div className="muted" style={{ fontSize: ".85rem", marginTop: 6 }}>
                {order.pickup_location_snapshot.notes}
              </div>
            )}
          </Card.Body></Card>
        </Section>
      )}

      {trackingLines.some((l) => (l.qty_closed || 0) > 0) && (
        <Section title="Residui chiusi su tua richiesta">
          <Card><Card.Body>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {trackingLines.filter((l) => (l.qty_closed || 0) > 0).map((l) => (
                <li key={l.order_line_id} data-testid={`closed-line-${l.sku}`}
                    style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}>
                  <strong>{l.name}</strong> ({l.sku}) — quantità non più prevista:{" "}
                  <strong>{l.qty_closed} cartoni</strong>
                </li>
              ))}
            </ul>
          </Card.Body></Card>
        </Section>
      )}

      {/* Righe ordinate con residui, se PARTIAL o se ci sono spedizioni parziali */}
      <Section title="Righe ordine" subtitle="Valori snapshot alla conferma — non ricalcolati con i listini/promozioni correnti">
        <div className="ds-table-wrap">
          <table className="ds-table" data-testid="order-lines-table">
            <thead>
              <tr>
                <th>SKU</th><th>Prodotto</th><th>Cart. ord.</th>
                <th>kg/cart.</th><th>kg tot.</th>
                <th>€/kg</th><th>Sconto</th><th>Totale riga</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((l) => {
                const isFree = l.line_kind === "FREE";
                const kgCart = Number(l.product_snapshot?.kg_per_carton || 0);
                const priceBase = Number(l.unit_price_base || 0);
                const priceEff = Number(l.unit_price_effective || 0);
                const hasDiscount = !isFree && priceEff < priceBase && priceBase > 0;
                // Pallet-mono discount snapshot: read-only from applied_promotions
                // (already customer-friendly by design — no repricing).
                const monoLine = (l.applied_promotions || []).find(
                  p => String(p?.type || "").toUpperCase() === "PALLET_MONO"
                );
                const monoAmt = monoLine ? Number(monoLine.amount || 0) : 0;
                return (
                  <tr key={l.id} data-testid={`order-line-${l.product_snapshot?.sku}`}
                      style={isFree ? { background: "#F0F9FF" } : undefined}>
                    <td>
                      {l.product_snapshot?.sku}
                      {isFree && (
                        <Badge tone="info" style={{ marginLeft: 6 }} data-testid={`omaggio-badge-${l.product_snapshot?.sku}`}>
                          OMAGGIO
                        </Badge>
                      )}
                    </td>
                    <td>
                      {l.product_snapshot?.name}
                      {isFree && l.free_gift_snapshot && (
                        <div style={{ fontSize: 11, color: "#0369A1", marginTop: 2 }}>
                          da promo <code>{l.free_gift_snapshot.promotion_code || l.free_gift_snapshot.contract_code}</code>
                          {" "}({l.free_gift_snapshot.buy_cartons}+{l.free_gift_snapshot.free_cartons})
                        </div>
                      )}
                    </td>
                    <td>{l.qty_cartons}</td>
                    <td>{kgCart ? kgCart.toFixed(3) : "—"}</td>
                    <td data-testid={`ol-kgtot-${l.product_snapshot?.sku}`}>{Number(l.line_weight_kg || 0).toFixed(1)}</td>
                    <td data-testid={`ol-eurkg-${l.product_snapshot?.sku}`}>
                      {isFree ? "—" : (
                        hasDiscount ? (
                          <span>
                            <span style={{ textDecoration: "line-through", color: "#94a3b8", fontSize: ".8rem" }}>{money(priceBase)}</span>{" "}
                            <strong style={{ color: "#059669" }}>{money(priceEff)}</strong>
                          </span>
                        ) : money(priceEff)
                      )}
                    </td>
                    <td data-testid={`ol-discount-${l.product_snapshot?.sku}`}>
                      {isFree ? "—" : (Number(l.line_discount) > 0 ? `-${money(l.line_discount)}` : "—")}
                    </td>
                    <td data-testid={`ol-linetotal-${l.product_snapshot?.sku}`}>
                      {isFree ? <strong style={{ color: "#059669" }}>€ 0,00</strong> : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                          <span>{money(l.line_total)}</span>
                          {monoAmt > 0 && (
                            <span data-testid={`ol-mono-saving-${l.product_snapshot?.sku}`}
                                  style={{ fontSize: ".72rem", color: "#0F7B4A", marginTop: 2 }}>
                              Risparmio pallet mono: -{money(monoAmt)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>Totali</td>
                <td data-testid="order-lines-total-kg" style={{ fontWeight: 600 }}>
                  {detail.lines.reduce((s, l) => s + Number(l.line_weight_kg || 0), 0).toFixed(1)}
                </td>
                <td></td>
                <td data-testid="order-total-discount-lines" style={{ fontWeight: 600 }}>
                  -{money(detail.lines.reduce((s, l) => s + Number(l.line_discount || 0), 0))}
                </td>
                <td data-testid="order-lines-total-eur" style={{ fontWeight: 600 }}>
                  {money(detail.lines.reduce((s, l) => s + Number(l.line_total || 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>

      {/* Riepilogo economico */}
      <Section title="Riepilogo economico">
        <Card data-testid="order-econ-summary"><Card.Body>
          <table className="ds-table" style={{ marginBottom: 0 }}>
            <tbody>
              <tr><td>Subtotale merce</td><td style={{ textAlign: "right" }} data-testid="econ-subtotal">{money(detail.subtotal)}</td></tr>
              {Number(detail.discount_total) > 0 && (
                <tr><td>Sconti prodotto / promozioni</td><td style={{ textAlign: "right", color: "#059669" }} data-testid="econ-discounts">-{money(detail.discount_total)}</td></tr>
              )}
              <tr><td>Contributo trasporto</td><td style={{ textAlign: "right" }} data-testid="econ-transport">{money(detail.transport_cost)}</td></tr>
              <tr style={{ borderTop: "2px solid #0F4C81" }}>
                <td style={{ fontWeight: 700, fontSize: "1.05rem", paddingTop: 8 }}>Totale ordine</td>
                <td style={{ textAlign: "right", fontWeight: 700, fontSize: "1.05rem", paddingTop: 8 }} data-testid="econ-total">{money(detail.total)}</td>
              </tr>
            </tbody>
          </table>
          {detail.discount_decision_snapshot?.applied_discounts?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: ".85rem", background: "#F7FAFC", padding: 10, borderRadius: 6 }}>
              <strong>Sconti/promozioni applicati:</strong>
              <ul style={{ margin: "4px 0 0 1.2rem" }}>
                {detail.discount_decision_snapshot.applied_discounts.map((a, i) => (
                  <li key={i} data-testid={`econ-discount-${i}`}>
                    <strong>{discountLabelWithPct(a)}</strong>: -{money(a.amount)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(!detail.discount_decision_snapshot?.applied_discounts?.length) && (
            <div className="muted" style={{ marginTop: 8, fontSize: ".82rem" }}>
              Nessuna promozione applicata
            </div>
          )}
        </Card.Body></Card>
      </Section>

      {/* Shipments — one card each */}
      {shipments.length > 0 && (
        <Section title={`Spedizioni (${shipments.length})`}>
          {shipments.map((s) => (
            <ShipmentCard key={s.id} shipment={s} lines={trackingLines} />
          ))}
        </Section>
      )}

      {contractSource && (contractSource.contract_code || contractSource.advance_payment_discount ||
                            contractSource.pallet_mono_discount || contractSource.contract_promotions_applied?.length ||
                            contractSource.free_gifts_generated?.length) && (
        <Section title="📋 Condizioni contrattuali applicate">
          <Card data-testid="contract-source-panel"><Card.Body>
            {contractSource.contract_code ? (
              <div style={{ marginBottom: 8 }}>
                Contratto: <code>{contractSource.contract_code}</code>
                {contractSource.source === "COOP_CONTRACT" && (
                  <Badge tone="info" style={{ marginLeft: 6 }}
                         data-testid="contract-source-coop-badge">
                    🏛️ Cooperativa · {contractSource.aggregation_mode === "GROUP_AGGREGATED" ? "Target Gruppo" : "Target Individuale"}
                  </Badge>
                )}
                {contractSource.source === "DIRECT_CONTRACT" && (
                  <Badge tone="neutral" style={{ marginLeft: 6 }}>Cliente diretto</Badge>
                )}
              </div>
            ) : (
              <div style={{ color: "#666", marginBottom: 8, fontStyle: "italic" }}>
                Nessun contratto attivo — sono state applicate solo regole globali.
              </div>
            )}
            {contractSource.advance_payment_discount && (
              <div>💳 Sconto pagamento anticipato: <strong>-{money(contractSource.advance_payment_discount.amount)}</strong>
                {" "}<span className="muted">({contractSource.advance_payment_discount.source})</span>{" "}
                — {contractSource.advance_payment_discount.rule}
              </div>
            )}
            {contractSource.pallet_mono_discount && (
              <div>🚚 Sconto pallet monoreferenza: <strong>-{money(contractSource.pallet_mono_discount.amount)}</strong>
                {" "}<span className="muted">({contractSource.pallet_mono_discount.source})</span>{" "}
                — {contractSource.pallet_mono_discount.rule}
              </div>
            )}
            {contractSource.contract_promotions_applied?.length > 0 && (
              <div style={{ marginTop: 6 }}>
                🎯 Promo contrattuali applicate ({contractSource.contract_promotions_applied.length}):
                <ul style={{ margin: "4px 0 0 1.2rem" }}>
                  {contractSource.contract_promotions_applied.map((p, i) => (
                    <li key={i}>{p.product_sku} — {p.type || "promo"}: -{money(p.amount || 0)}</li>
                  ))}
                </ul>
              </div>
            )}
            {contractSource.free_gifts_generated?.length > 0 && (
              <div style={{ marginTop: 6 }}>
                🎁 Cartoni omaggio generati ({contractSource.free_gifts_generated.length}):
                <ul style={{ margin: "4px 0 0 1.2rem" }}>
                  {contractSource.free_gifts_generated.map((g, i) => (
                    <li key={i}>{g.qty} × {g.gift_sku} (promo <code>{g.promotion_code || g.contract_code}</code>{" "}
                      {g.buy_cartons}+{g.free_cartons})</li>
                  ))}
                </ul>
              </div>
            )}
            {contractSource.applied_fields && Object.keys(contractSource.applied_fields).length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#0369A1" }}>
                Fonte contratto: {Object.entries(contractSource.applied_fields).map(([k, v]) => `${k}=${v}`).join(", ")}
              </div>
            )}
            {contractSource.fallback_fields?.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>
                Fallback su regole globali per: {contractSource.fallback_fields.join(", ")}
              </div>
            )}
          </Card.Body></Card>
        </Section>
      )}

      {detail.discount_decision_snapshot?.applied_discounts?.length > 0 && (
        <Section title="Sconti applicati">
          <Card><Card.Body>
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {detail.discount_decision_snapshot.applied_discounts.map((a, i) => (
                <li key={i}><strong>{discountLabelWithPct(a)}</strong>: -{money(a.amount)}</li>
              ))}
            </ul>
          </Card.Body></Card>
        </Section>
      )}
    </>
  );
}
